/**
 * Gate H5.3: Frontend Summary Normalization (Hardened)
 * 
 * Converts any summary content structure to a renderable format.
 * Provides resilient fallbacks so staff never sees raw JSON.
 * 
 * H5.3 Hardening guarantees:
 * - Idempotent: Canonical content passes through unchanged (no double truncation)
 * - Non-destructive: Bullets already truncated by backend stay as-is
 * - Frontend normalization only activates on non-canonical legacy shapes
 */

export interface NormalizedSummaryContent {
  interactionCount?: number;
  bullets: string[];
  trendNarrative: string;
}

/**
 * Check if content already matches canonical schema.
 * Canonical: { bullets: string[], trendNarrative: string }
 */
function isCanonicalSchema(obj: Record<string, unknown>): boolean {
  if (!Array.isArray(obj.bullets)) return false;
  if (obj.bullets.length === 0) return false;
  // All items in bullets must be strings
  for (const b of obj.bullets) {
    if (typeof b !== "string") return false;
  }
  // trendNarrative must be string (or undefined/missing is ok)
  if (obj.trendNarrative !== undefined && typeof obj.trendNarrative !== "string") {
    return false;
  }
  return true;
}

/**
 * Normalize summary content from any structure to bullets/trendNarrative.
 * This provides frontend resilience for legacy or unexpected formats.
 * 
 * IDEMPOTENT: If content is already in canonical form, it passes through unchanged.
 * NO DOUBLE TRUNCATION: Backend already truncates; frontend does not re-truncate.
 */
export function normalizeSummaryForRender(content: unknown): NormalizedSummaryContent {
  if (!content || typeof content !== "object") {
    return {
      bullets: ["Summary content unavailable"],
      trendNarrative: "",
    };
  }

  const obj = content as Record<string, unknown>;
  
  // H5.3 Hardening: Fast path for canonical content (idempotent pass-through)
  // If content is already canonical, return as-is without modification
  if (isCanonicalSchema(obj)) {
    return {
      interactionCount: typeof obj.interactionCount === "number" ? obj.interactionCount : undefined,
      bullets: obj.bullets as string[],
      trendNarrative: (obj.trendNarrative as string) || "",
    };
  }
  
  // Non-canonical content: extract and normalize
  const bullets: string[] = [];
  let trendNarrative = "";

  // Extract trend narrative from multiple possible locations
  if (typeof obj.trendNarrative === "string") {
    trendNarrative = obj.trendNarrative;
  } else if (obj.analysis && typeof obj.analysis === "object") {
    const analysis = obj.analysis as Record<string, unknown>;
    if (typeof analysis.narrative === "string") {
      trendNarrative = analysis.narrative;
    }
  }

  // Priority 1: bullets array exists but may have non-string items
  if (Array.isArray(obj.bullets) && obj.bullets.length > 0) {
    for (const b of obj.bullets) {
      if (typeof b === "string" && b.trim()) {
        bullets.push(b.trim());
      }
    }
    if (bullets.length > 0) {
      return { bullets, trendNarrative };
    }
  }

  // Priority 2: behaviorSummary array (legacy format)
  if (Array.isArray(obj.behaviorSummary)) {
    for (const entry of obj.behaviorSummary) {
      if (entry && typeof entry === "object") {
        const item = entry as Record<string, unknown>;
        
        // Extract trend with species prefix
        if (typeof item.trend === "string" && item.trend.trim()) {
          const species = typeof item.species === "string" ? item.species : "";
          const prefix = species ? `[${species}] ` : "";
          bullets.push(`${prefix}${item.trend}`.trim());
        }
        
        // Extract observations
        if (Array.isArray(item.observations)) {
          for (const obs of item.observations) {
            if (typeof obs === "string" && obs.trim()) {
              bullets.push(obs.trim());
            } else if (obs && typeof obs === "object") {
              const obsObj = obs as Record<string, unknown>;
              const text = (obsObj.note || obsObj.observation || obsObj.text) as string | undefined;
              if (typeof text === "string" && text.trim()) {
                bullets.push(text.trim());
              }
            }
          }
        }
      }
    }
    if (bullets.length > 0) {
      return { bullets: bullets.slice(0, 15), trendNarrative };
    }
  }

  // Priority 3: analysis.trends array (legacy format)
  if (obj.analysis && typeof obj.analysis === "object") {
    const analysis = obj.analysis as Record<string, unknown>;
    if (Array.isArray(analysis.trends)) {
      for (const t of analysis.trends) {
        if (typeof t === "string" && t.trim()) {
          bullets.push(t.trim());
        }
      }
      if (bullets.length > 0) {
        return { bullets: bullets.slice(0, 15), trendNarrative };
      }
    }
  }

  // Priority 4: summary string
  if (typeof obj.summary === "string" && obj.summary.trim()) {
    return {
      bullets: [obj.summary.trim()],
      trendNarrative,
    };
  }

  // Priority 5: Try to extract from any array-like property
  // CRITICAL: Only accept strings - never allow objects to pass through
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (Array.isArray(value) && bullets.length === 0) {
      for (const item of value) {
        // Only add string items - explicitly reject objects
        if (typeof item === "string" && item.trim()) {
          bullets.push(item.trim());
        }
        // Objects are silently skipped - never converted to strings
      }
    }
  }

  if (bullets.length > 0) {
    return { bullets: bullets.slice(0, 15), trendNarrative };
  }

  // Final fallback: human-readable message (never raw JSON)
  return {
    bullets: ["Summary content could not be displayed in standard format"],
    trendNarrative,
  };
}
